import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { updateUserProfile } from '../services/firebase/users';
import { logError } from '../utils/logger';

/**
 * Parses a comma-separated string into a trimmed array of non-empty strings.
 */
export function parseListItems(str) {
  return str.split(',').map((s) => s.trim()).filter(Boolean);
}

/**
 * Modal for editing a blacklist or whitelist.
 *
 * Props:
 *   title        – modal heading, e.g. "Blacklist Artists"
 *   description  – short help text shown below the heading
 *   placeholder  – textarea placeholder, e.g. "e.g. Rick Astley, Queen, BTS"
 *   currentItems – string[] currently active in the rule
 *   onApply      – (items: string[]) => void  called when the user clicks Apply
 *   onClose      – () => void
 */
export default function ListEditorModal({
  title,
  description,
  placeholder,
  currentItems,
  onApply,
  onClose,
}) {
  const { t } = useTranslation();
  const { user, profile } = useAuth();

  const savedLists = profile?.savedLists || [];

  const [inputValue, setInputValue] = useState((currentItems || []).join(', '));
  const [listName, setListName] = useState('');
  const [selectedListId, setSelectedListId] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);

  const parsedItems = parseListItems(inputValue);

  function handleLoadList(id) {
    setSelectedListId(id);
    if (!id) return;
    const list = savedLists.find((l) => l.id === id);
    if (list) {
      setInputValue(list.items.join(', '));
      setListName(list.name);
    }
  }

  async function handleSaveList() {
    const items = parseListItems(inputValue);
    if (!listName.trim() || items.length === 0) return;
    setSaving(true);
    try {
      const existingIdx = selectedListId
        ? savedLists.findIndex((l) => l.id === selectedListId)
        : -1;

      let newLists;
      if (existingIdx !== -1) {
        newLists = savedLists.map((l, i) =>
          i === existingIdx ? { ...l, name: listName.trim(), items } : l
        );
      } else {
        const newList = {
          id: crypto.randomUUID(),
          name: listName.trim(),
          items,
          createdAt: Date.now(),
        };
        newLists = [...savedLists, newList];
        setSelectedListId(newList.id);
      }

      await updateUserProfile(user.uid, { savedLists: newLists });
      setSavedMsg(true);
      setTimeout(() => setSavedMsg(false), 2500);
    } catch (err) {
      logError(err);
    } finally {
      setSaving(false);
    }
  }

  function handleApply() {
    onApply(parseListItems(inputValue));
  }

  function handleClear() {
    setInputValue('');
    setSelectedListId('');
    setListName('');
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.55)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="w-full max-w-md t-bg-surface t-border border rounded-2xl p-6 space-y-4 shadow-xl">
        {/* Header */}
        <div>
          <h2 className="text-lg font-semibold t-text">{title}</h2>
          <p className="text-xs t-text-disabled mt-1">{description}</p>
        </div>

        {/* Load from saved list */}
        {savedLists.length > 0 && (
          <div>
            <label
              htmlFor="list-editor-load"
              className="block text-xs font-medium t-text-secondary mb-1"
            >
              {t('gameRules.loadFromSaved')}
            </label>
            <select
              id="list-editor-load"
              value={selectedListId}
              onChange={(e) => handleLoadList(e.target.value)}
              className="w-full t-bg-input t-border-input border rounded-lg px-3 py-2 t-text text-sm t-focus-input"
            >
              <option value="">{t('gameRules.selectSavedList')}</option>
              {savedLists.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Comma-separated text input */}
        <div>
          <label
            htmlFor="list-editor-items"
            className="block text-xs font-medium t-text-secondary mb-1"
          >
            {t('gameRules.listItems')}
          </label>
          <textarea
            id="list-editor-items"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={placeholder}
            rows={4}
            className="w-full t-bg-input t-border-input border rounded-lg px-3 py-2 t-text t-placeholder text-sm t-focus-input resize-none"
          />
          <p className="text-xs t-text-disabled mt-1">
            {parsedItems.length > 0
              ? t('gameRules.itemsCount', { count: parsedItems.length })
              : t('gameRules.noItems')}
          </p>
        </div>

        {/* Save section */}
        <div className="space-y-2">
          <label
            htmlFor="list-editor-name"
            className="block text-xs font-medium t-text-secondary"
          >
            {t('gameRules.saveAsLabel')}
          </label>
          <div className="flex gap-2">
            <input
              id="list-editor-name"
              type="text"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder={t('gameRules.listNamePlaceholder')}
              maxLength={50}
              className="flex-1 t-bg-input t-border-input border rounded-lg px-3 py-2 t-text t-placeholder text-sm t-focus-input"
            />
            <button
              type="button"
              onClick={handleSaveList}
              disabled={saving || !listName.trim() || parsedItems.length === 0}
              className="t-btn-primary disabled:opacity-50 font-semibold rounded-lg px-4 py-2 text-sm transition-colors t-focus-ring shrink-0"
            >
              {saving ? '…' : t('gameRules.saveList')}
            </button>
          </div>
          {savedMsg && (
            <p className="text-xs t-text-accent">{t('gameRules.listSaved')}</p>
          )}
        </div>

        {/* Action row */}
        <div className="flex items-center justify-between gap-3 pt-1 border-t t-border-input">
          <button
            type="button"
            onClick={handleClear}
            className="text-sm t-text-muted hover:t-text transition-colors t-focus-ring rounded"
          >
            {t('gameRules.clearList')}
          </button>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="text-sm t-text-muted hover:t-text transition-colors t-focus-ring rounded px-3 py-2"
            >
              {t('game.cancel')}
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="t-btn-primary font-semibold rounded-lg px-4 py-2 text-sm transition-colors t-focus-ring"
            >
              {t('gameRules.apply')}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
